import PropTypes from 'prop-types';
import { merge } from 'lodash';
import ReactApexChart from 'react-apexcharts';
// material
import { Card, CardHeader, Box, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import downloadFill from '@iconify/icons-eva/download-fill';
//
import { useTranslation } from 'react-i18next';
import { BaseOptionChart } from './charts';

function RecommendationsChart({ title, subtitle, productViews, recommendations, labels }) {
  const { t } = useTranslation();

  const handleDownload = () => {
    // Create CSV content
    const headers = [
      'Date',
      t('overview.number_of_product_views'),
      t('overview.number_of_recommendations')
    ];
    const rows = labels.map((label, index) => [label, productViews[index], recommendations[index]]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${title.toLowerCase().replace(/\s+/g, '_')}_data.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const CHART_DATA = [
    {
      name: t('overview.number_of_product_views'),
      type: 'bar',
      data: productViews
    },
    {
      name: t('overview.number_of_recommendations'),
      type: 'bar',
      data: recommendations
    }
  ];

  const chartOptions = merge(BaseOptionChart(), {
    stroke: { width: [0, 0] },
    plotOptions: { bar: { columnWidth: '11%', borderRadius: 4 } },
    fill: { type: ['solid', 'solid'] },
    labels: [...labels],
    xaxis: { type: 'datetime' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y) => {
          if (typeof y !== 'undefined') {
            return `${y.toFixed(0)}`;
          }
          return y;
        }
      }
    }
  });

  return (
    <Card>
      <CardHeader
        title={title}
        subheader={subtitle}
        action={
          <Tooltip title={t('general.download_data')}>
            <IconButton onClick={handleDownload}>
              <Icon icon={downloadFill} />
            </IconButton>
          </Tooltip>
        }
      />
      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart type="line" series={CHART_DATA} options={chartOptions} height={364} />
      </Box>
    </Card>
  );
}

RecommendationsChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  productViews: PropTypes.arrayOf(PropTypes.number).isRequired,
  recommendations: PropTypes.arrayOf(PropTypes.number).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired
};

RecommendationsChart.defaultProps = {
  subtitle: ''
};

export default RecommendationsChart;
